import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, Phone, Send } from "lucide-react";
import Modal from "@/components/Modal";
import Reveal from "@/components/Reveal";
import { HERO_CONTACT } from "@/config/heroImages";

/**
 * Public "Contact" page. Submissions are not wired to a backend yet — the
 * form shows a confirmation modal and clears.
 */
const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [modalVisible, setModalVisible] = useState(false);

  const handleSubmit = () => {
    setModalVisible(true);
    setName("");
    setEmail("");
    setMessage("");
  };

  return (
    <div>
      <section
        className="relative bg-cover bg-center py-16 md:py-20"
        style={{ backgroundImage: `url(${HERO_CONTACT})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/40 to-[#ffa217]/20" />
        <div className="relative z-10 mx-auto max-w-3xl animate-fade-down px-4 text-center">
          <h1 className="text-4xl font-extrabold text-white md:text-5xl">
            {t("Contact Us")}
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-gray-200">
            {t("Questions, feedback or partnership ideas? We would love to hear from you.")}
          </p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <Reveal className="mx-auto grid max-w-6xl gap-10 px-4 md:grid-cols-2">
          <div>
            <h2 className="text-2xl font-extrabold text-[#333]">
              {t("Get in touch")}
            </h2>
            <div className="mt-6 space-y-4">
              <div className="flex items-center gap-4 rounded-2xl border border-gray-100 p-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#fff3e0] text-[#ffa217]">
                  <Mail size={20} />
                </span>
                <div>
                  <div className="text-sm text-gray-500">{t("Email")}</div>
                  <a href="mailto:enquiry@we2tech.pro" className="font-semibold text-[#333] hover:text-[#ffa217]">
                    enquiry@we2tech.pro
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4 rounded-2xl border border-gray-100 p-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#fff3e0] text-[#ffa217]">
                  <Phone size={20} />
                </span>
                <div>
                  <div className="text-sm text-gray-500">{t("Phone")}</div>
                  <a href="tel:+85253968435" className="font-semibold text-[#333] hover:text-[#ffa217]">
                    +852 53968435
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4 rounded-2xl border border-gray-100 p-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#fff3e0] text-[#ffa217]">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.72 3.72 0 0 1-1.38-.9 3.72 3.72 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41 1.27-.06 1.65-.07 4.85-.07zM12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63a5.9 5.9 0 0 0-2.13 1.38A5.9 5.9 0 0 0 .63 4.14C.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.8.72 1.47 1.38 2.13a5.9 5.9 0 0 0 2.13 1.38c.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.9 5.9 0 0 0 2.13-1.38 5.9 5.9 0 0 0 1.38-2.13c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.9 5.9 0 0 0-1.38-2.13A5.9 5.9 0 0 0 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0zm0 5.84A6.16 6.16 0 1 0 18.16 12 6.16 6.16 0 0 0 12 5.84zM12 16a4 4 0 1 1 4-4 4 4 0 0 1-4 4zm7.85-10.4a1.44 1.44 0 1 1-1.44-1.44 1.44 1.44 0 0 1 1.44 1.44z" />
                  </svg>
                </span>
                <div>
                  <div className="text-sm text-gray-500">{t("Social")}</div>
                  <a
                    href="https://www.instagram.com/uninfohk/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-[#333] hover:text-[#ffa217]"
                  >
                    @uninfohk
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-gray-100 bg-[#fff8f0] p-8">
            <h2 className="text-2xl font-extrabold text-[#333]">
              {t("Send us a message")}
            </h2>
            <div className="mt-6 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {t("Name")}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffa217]"
                  placeholder={t("Your name")}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {t("Email")}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffa217]"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {t("Message")}
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffa217]"
                  placeholder={t("How can we help?")}
                />
              </div>
              <button
                type="button"
                onClick={handleSubmit}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#ffa217] py-3 font-bold text-white transition hover:bg-[#e08f0e]"
              >
                <Send size={18} />
                {t("Send Message")}
              </button>
            </div>
          </div>
        </Reveal>
      </section>

      <Modal visible={modalVisible} onClose={() => setModalVisible(false)}>
        <div className="rounded-2xl bg-white p-8 text-center shadow-lg">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#fff3e0] text-[#ffa217]">
            <Mail size={26} />
          </span>
          <div className="mt-4 text-xl font-bold text-[#333]">
            {t("Message sent")}
          </div>
          <p className="mt-2 text-sm leading-relaxed text-gray-600">
            {t("Thank you for reaching out. We will get back to you soon.")}
          </p>
          <button
            type="button"
            onClick={() => setModalVisible(false)}
            className="mt-6 rounded-lg bg-[#ffa217] px-6 py-2.5 font-semibold text-white transition hover:bg-[#e08f0e]"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ContactPage;
